/**
 * The sidebar icon set.
 *
 * One family, drawn to the same rules so the column reads as a single thing:
 * a 24-unit box, 1.75 stroke, round caps and joins, no fills. Every path is
 * stroked in `currentColor`, so an icon takes the colour of the nav item it
 * sits in — muted at rest, brand green when active — and there is no second
 * place to update when those colours change.
 *
 * They replaced emoji, which each arrive in their own palette and weight and
 * cannot be told to match anything.
 */
const PATHS = {
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </>
  ),
  transport: (
    <>
      <path d="M2 6h12v10H2z" />
      <path d="M14 10h4l3 3v3h-7" />
      <circle cx="6.5" cy="17.5" r="1.8" />
      <circle cx="17.5" cy="17.5" r="1.8" />
    </>
  ),
  orders: (
    <>
      <path d="M9 4H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="2.5" width="6" height="3.5" rx="1" />
      <path d="M9 11h6M9 15h4" />
    </>
  ),
  customers: (
    <>
      <circle cx="9" cy="8" r="3.25" />
      <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.5a3.25 3.25 0 0 1 0 6.3" />
      <path d="M17.5 14.5a5.5 5.5 0 0 1 3 5" />
    </>
  ),
  products: (
    <>
      <path d="M21 8.5 12 13 3 8.5 12 4z" />
      <path d="M3 8.5v7L12 20l9-4.5v-7" />
      <path d="M12 13v7" />
    </>
  ),
  categories: (
    <>
      <path d="M3.5 11.2V4.5a1 1 0 0 1 1-1h6.7a1 1 0 0 1 .7.3l8 8a1 1 0 0 1 0 1.4l-6.7 6.7a1 1 0 0 1-1.4 0l-8-8a1 1 0 0 1-.3-.7z" />
      <circle cx="7.75" cy="7.75" r="1.25" />
    </>
  ),
  units: (
    <>
      <path d="M12 4v16M7 20h10" />
      <path d="M4 8h16" />
      <path d="M4 8 2 13.5a3 3 0 0 0 6 0z" />
      <path d="M20 8l-2 5.5a3 3 0 0 0 6 0z" transform="translate(-2)" />
    </>
  ),
  // A shop weight — ring handle over a tapered block. Distinct from `units`,
  // which is a balance: that page names units, this one says what they weigh.
  weights: (
    <>
      <path d="M9.5 8a2.5 2.5 0 1 1 5 0" />
      <path d="M8.6 8h6.8a1 1 0 0 1 1 .85l1.35 9.9a1 1 0 0 1-1 1.25H7.25a1 1 0 0 1-1-1.25L7.6 8.85a1 1 0 0 1 1-.85z" />
    </>
  ),
  testbench: (
    <>
      <path d="M10 3v6.2L4.8 18A2 2 0 0 0 6.5 21h11a2 2 0 0 0 1.7-3L14 9.2V3" />
      <path d="M8.5 3h7" />
      <path d="M7 15h10" />
    </>
  ),
  ai: (
    <>
      <rect x="6" y="6" width="12" height="12" rx="2.5" />
      <path d="M10 10h4v4h-4z" />
      <path d="M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3" />
    </>
  ),
  messages: (
    <>
      <path d="M20 12a7.5 7.5 0 0 1-10.9 6.7L4 20l1.3-4.1A7.5 7.5 0 1 1 20 12z" />
      <path d="M9 11.5h6M9 14h3.5" />
    </>
  ),
  reports: (
    <>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <path d="M8 20v-6M12.5 20V8M17 20v-9" />
    </>
  ),
  users: (
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </>
  ),
  roles: (
    <>
      <path d="M12 3 5 6v5.5c0 4 2.9 7.7 7 9.5 4.1-1.8 7-5.5 7-9.5V6z" />
      <path d="m9.5 12 1.8 1.8 3.4-3.6" />
    </>
  ),
  // A circular arrow: reset. A funnel with the filtering crossed out was the
  // obvious drawing and it does not survive the size it is used at —
  // rendered at 17px the two shapes collapse into a smudge. One shape, like
  // everything else in this set.
  clearFilter: (
    <>
      {/* A funnel with the filtering taken off it. The circular arrow this
          replaced said 'reset', which is also what a reload button says.

          The funnel is drawn full width — the mouth spans the box, the same
          as every other icon here. The badge sits in the empty wedge under
          the right-hand slant, and the stem stops half a unit short of it:
          at 17px two shapes that touch read as one smudge, and nothing has
          to be shrunk to buy that gap. */}
      <path d="M2.5 4 H20 L12.5 12.5 V18 L9 16 V12.5 Z" />
      <circle cx="18.5" cy="18" r="4.25" />
      <path d="M16.85 16.35 L20.15 19.65 M20.15 16.35 L16.85 19.65" />
    </>
  ),
  // A pencil: change this. For a small action beside what it changes.
  edit: (
    <>
      <path d="M16.5 3.5 L20.5 7.5 L8 20 H4 V16 Z" />
      <path d="M14 6 L18 10" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 14a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V20a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 9 18.4a1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.6 9a1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" />
    </>
  ),
}

export default function Icon({ name, className = '' }) {
  const paths = PATHS[name]
  if (!paths) return null
  return (
    <svg
      className={`icon ${className}`.trim()}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {paths}
    </svg>
  )
}
